import React, { useEffect } from "react";
import { CircularProgress } from "@remo-co/ui-core/src/components/CircularProgress";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import classNames from "classnames";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import { selectCompanySettings } from "modules/companySettings/redux/selectors";
import { ICompanySettings, IIdentityProvider } from "modules/company/types";
import { useStyles } from "./styles";
import InfoContainer from "./InfoContainer";
import Activation from "./Activation";
import SamlConfiguration from "./SamlConfiguration";
import useSso from "./hooks/useSso";

export enum ISsoStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PENDING = "pending",
}

interface ISsoConfigurationProps {
  tabPanelRef?: React.RefObject<HTMLDivElement>;
}
const SsoConfiguration = ({ tabPanelRef }: ISsoConfigurationProps) => {
  const {
    configInfo,
    isNew,
    isReady,
    ssoStatus,
    isNewlyCreated,
    fetchProvider,
    onSyncProvider,
  } = useSso();
  const { t } = useI18n(["common", "sso"]);
  const classes = useStyles();
  const companySettings: ICompanySettings = useSelector(selectCompanySettings);
  const SamlProvider = (
    companySettings?.identityProviders?.type === "saml2"
      ? companySettings.identityProviders
      : {}
  ) as IIdentityProvider;

  useEffect(() => {
    if (SamlProvider.providerId) {
      fetchProvider(SamlProvider.providerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SamlProvider?.providerId]);

  return (
    <>
      <div>
        <Typography variant="h3">
          {t("sso:title")}
          {!isReady && (
            <CircularProgress
              size={20}
              className={classNames("loader", classes.spinner)}
            />
          )}
        </Typography>
      </div>

      <Container maxWidth="lg" className={classes.container}>
        {!isNew && (
          <Activation
            isReady={isReady}
            ssoStatus={ssoStatus}
            onSyncProvider={onSyncProvider}
          />
        )}

        {/* SECTION :: Primary Info */}
        <InfoContainer
          isReady={isReady}
          configInfo={configInfo}
          ssoStatus={ssoStatus}
          isNew={isNew}
          onSyncProvider={onSyncProvider}
        />

        {/* SECTION :: SAML Configuration */}
        {!isNew && (
          <SamlConfiguration
            tabPanelRef={tabPanelRef}
            isReady={isReady}
            showHelperDialog={isNewlyCreated}
            samlInfo={{
              serviceProviderEntityID: configInfo.serviceProviderEntityID,
              acsUrl: configInfo.acsUrl,
            }}
          />
        )}
      </Container>
    </>
  );
};

export default SsoConfiguration;
